import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Heart, Mail, Lock, User, AlertCircle } from "lucide-react";
import { apiClient } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (userData: { id: string; email: string; name?: string }, isSignup?: boolean) => void;
  character?: {
    name: string;
    avatar: string;
    personality: string;
    communicationStyle: string;
    style: string;
  };
}

export const AuthModal = ({ isOpen, onClose, onComplete, character }: AuthModalProps) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      await apiClient.post('/auth/password/reset', { email });
      {
        toast({
          title: "Password reset email sent! 📧",
          description: "Check your inbox for the password reset link.",
        });
        setIsForgotPassword(false);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const validatePassword = (password: string) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("at least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("one uppercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("one number");
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("one special character");
    }
    return errors;
  };

  const passwordErrors = !isLogin ? validatePassword(password) : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const response = await apiClient.post('/auth/login', { email, password });
        const user = response?.user || response; // support either shape
        if (!user?.id || !user?.email) {
          setError('Invalid login response');
          return;
        }
        // Optionally handle tokens if returned
        if (response?.accessToken) {
          apiClient.setToken(response.accessToken);
        }
        onComplete({ id: user.id, email: user.email, name: user.name }, false);
        toast({
          title: "Welcome back! 💕",
          description: "You've successfully logged in.",
        });
      } else {
        const response = await apiClient.post('/auth/signup', { email, password, name });
        const user = response?.user || response; // support either shape
        if (!user?.id || !user?.email) {
          setError('Invalid signup response');
          return;
        }
        onComplete({ id: user.id, email: user.email, name: user.name }, true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-gradient-dreamy border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
            {isForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back!' : 'Join the Romance'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="text-center mb-6">
          <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-primary/10">
            <Heart className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {isForgotPassword ? (
                'Enter your email address and we\'ll send you a link to reset your password.'
              ) : character ? (
                <>
                  {character.name} is waiting to meet you! 
                  {isLogin ? ' Sign in to continue your story.' : ' Create your account to begin chatting.'}
                </>
              ) : (
                isLogin ? 'Welcome back! Sign in to continue.' : 'Create your account to get started.'
              )}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {isForgotPassword ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 border-primary/20 focus:border-primary"
                  required
                />
              </div>
            </div>
            
            <Button 
              onClick={handleForgotPassword} 
              variant="romantic" 
              className="w-full" 
              disabled={forgotPasswordLoading}
            >
              <Mail className="h-4 w-4 mr-2" />
              {forgotPasswordLoading ? 'Sending...' : 'Send Reset Email'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Your Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 border-primary/20 focus:border-primary"
                  required
                />
              </div>
            </div>
          )}

          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="age" className="text-sm font-medium">
                Your Age
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="age"
                  type="number"
                  placeholder="Enter your age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="pl-10 border-primary/20 focus:border-primary"
                  min="13"
                  max="100"
                  required
                />
              </div>
               {age && parseInt(age) < 13 && (
                 <p className="text-xs text-destructive">You must be 13 or older to use this app.</p>
               )}
            </div>
          )}
          
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 border-primary/20 focus:border-primary"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 border-primary/20 focus:border-primary"
                required
              />
            </div>
            {!isLogin && password && passwordErrors.length > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                <p className="mb-1">Password must include:</p>
                <ul className="space-y-1">
                  {passwordErrors.map((error, index) => (
                    <li key={index} className="flex items-center gap-1">
                      <span className="text-destructive">✗</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!isLogin && password && passwordErrors.length === 0 && (
              <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <span>✓</span>
                Password meets all requirements
              </div>
            )}
            {isLogin && (
              <button
                type="button"
                onClick={() => setIsForgotPassword(true)}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Forgot your password?
              </button>
            )}
          </div>
          
            <Button 
              type="submit" 
              variant="romantic" 
              className="w-full" 
              disabled={loading || (!isLogin && passwordErrors.length > 0)}
            >
              <Heart className="h-4 w-4 mr-2" />
              {loading ? 'Loading...' : isLogin ? 
                (character ? `Chat with ${character.name}` : 'Sign In') : 
                'Create Account'
              }
            </Button>
          </form>
        )}
        
        <div className="text-center space-y-2">
          {!isForgotPassword && (
            <>
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </>
          )}
          
          {isForgotPassword && (
            <button
              type="button"
              onClick={() => {
                setIsForgotPassword(false);
                setError('');
              }}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Back to Sign In
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
